import { NgxMatMomentAdapter, NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular-material-components/moment-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import * as moment from 'moment-timezone';
import { Injectable, Optional, Inject } from '@angular/core';

@Injectable()
export class MomentTimezoneDateAdapter extends NgxMatMomentAdapter {
    public static appTimeZone: string = 'UTC';

    constructor(
        @Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string,
        @Optional() @Inject(NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS) options?: { useUtc: boolean }
    ) {
        super(dateLocale, { ...options, useUtc: false });
    }

    createDate(year: number, month: number, date: number): moment.Moment {
        if (month < 0 || month > 11) {
            throw Error(`Invalid month index "${month}". Month index must be between 0 and 11.`);
        }

        const result = moment.tz(
            {
                year,
                month,
                date,
                hour: 0,
                minute: 0,
                second: 0,
                millisecond: 0
            },
            MomentTimezoneDateAdapter.appTimeZone
        );

        if (!result.isValid()) {
            return moment.tz(MomentTimezoneDateAdapter.appTimeZone).startOf('day');
        }
        return result;
    }

    setTimezone() {
        moment.tz.setDefault(MomentTimezoneDateAdapter.appTimeZone);
    }

    parse(value: any, parseFormat: string | string[]): moment.Moment | null {
        if (typeof value === 'string' && value.length > 0) {
            const momentObj = moment.tz(value, parseFormat, MomentTimezoneDateAdapter.appTimeZone);
            return momentObj.isValid() ? momentObj : null;
        } else if (moment.isMoment(value)) {
            return value.clone().tz(MomentTimezoneDateAdapter.appTimeZone);
        } else if (value instanceof Date) {
            return moment.tz(value, MomentTimezoneDateAdapter.appTimeZone);
        }
        return null;
    }

    compareDateWithTime(first: moment.Moment, second: moment.Moment): number {
        const firstTz = first ? first.clone().tz(MomentTimezoneDateAdapter.appTimeZone) : null;
        const secondTz = second ? second.clone().tz(MomentTimezoneDateAdapter.appTimeZone) : null;

        if (!firstTz || !secondTz) {
            return !firstTz && !secondTz ? 0 : !firstTz ? -1 : 1;
        }

        if (firstTz.isBefore(secondTz)) return -1;
        if (firstTz.isAfter(secondTz)) return 1;
        return 0;
    }

    today(): moment.Moment {
        return moment.tz(MomentTimezoneDateAdapter.appTimeZone);
    }

    copyTime(source: moment.Moment, target: moment.Moment): moment.Moment {
        if (!source || !target) return target;

        const sourceTz = source.clone().tz(MomentTimezoneDateAdapter.appTimeZone);
        return target
            .clone()
            .tz(MomentTimezoneDateAdapter.appTimeZone)
            .hour(sourceTz.hour())
            .minute(sourceTz.minute())
            .second(sourceTz.second())
            .millisecond(sourceTz.millisecond());
    }

    deserialize(value: any): moment.Moment | null {
        if (value instanceof Date) {
            return moment.tz(value, MomentTimezoneDateAdapter.appTimeZone);
        }
        if (typeof value === 'string') {
            const m = moment.tz(value, MomentTimezoneDateAdapter.appTimeZone);
            return m.isValid() ? m : null;
        }
        if (moment.isMoment(value)) {
            return value.clone().tz(MomentTimezoneDateAdapter.appTimeZone);
        }
        return null;
    }

    clampDate(date: moment.Moment, min?: moment.Moment | null, max?: moment.Moment | null): moment.Moment {
        let clampedDate = date.clone().tz(MomentTimezoneDateAdapter.appTimeZone);

        if (min && clampedDate.isBefore(min)) {
            clampedDate = min.clone().tz(MomentTimezoneDateAdapter.appTimeZone);
        }

        if (max && clampedDate.isAfter(max)) {
            clampedDate = max.clone().tz(MomentTimezoneDateAdapter.appTimeZone);
        }

        return clampedDate;
    }

    format(date: moment.Moment, displayFormat: string): string {
        return date.clone().tz(MomentTimezoneDateAdapter.appTimeZone).format(displayFormat);
    }
}
